// react
import React, { useEffect } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// qrcode.react
import { QRCodeSVG } from "qrcode.react";

// @mui
import { Box, SwipeableDrawer, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button, IconButton, Typography, useMediaQuery, useTheme, Tooltip, Chip } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import { Puller } from "ui-components/Puller";

// A ---------------------------------------------------------------------- M

const TagQRCode = ({ tag, open, setOpen }) => {
  const { conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleReset = () => {
    setOpen(false);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "", "width=600,height=400");
    const qrCode = document.querySelector("#qr-code-svg");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Tag QR Code</title>
          <style>
            @media print {
              body * { visibility: hidden; }
              #qr-code-svg, #qr-code-svg * { visibility: visible; }
              #qr-code-svg { position: absolute; left: 250px; top: 50px; }
            }
            .close-button {
              position: fixed;
              top: 20px;
              right: 20px;
              padding: 12px 24px;
              font-size: 45px;
              font-weight: bold;
              background-color: #e0e0e0;
              border: none;
              cursor: pointer;
            }
          </style>
        </head>
        <body>
          <button class="close-button" onclick="window.close()">Close window</button>
          ${qrCode.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    setOpen(false);
  };

  useEffect(() => {
    const handlePopstate = () => open && handleReset();
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open, setOpen]);

  const printForm = (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label="1"
            size="small"
            sx={{
              height: 24,
              minWidth: 24,
              borderRadius: "50%",
              fontWeight: 700,
              bgcolor: `${theme.palette.primary.main}22`,
              color: theme.palette.primary.main,
            }}
          />
          <Typography variant="body1" sx={{ color: "text.primary" }}>
            {t("qr_code_steps_1")}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label="2"
            size="small"
            sx={{
              height: 24,
              minWidth: 24,
              borderRadius: "50%",
              fontWeight: 700,
              bgcolor: `${theme.palette.primary.main}22`,
              color: theme.palette.primary.main,
            }}
          />
          <Typography variant="body1" sx={{ color: "text.primary" }}>
            {t("qr_code_steps_2")}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label="3"
            size="small"
            sx={{
              height: 24,
              minWidth: 24,
              borderRadius: "50%",
              fontWeight: 700,
              bgcolor: `${theme.palette.primary.main}22`,
              color: theme.palette.primary.main,
            }}
          />
          <Typography variant="body1" sx={{ color: "text.primary" }}>
            {conservSostL1 ? t("qr_code_steps_3") : t("qr_code_steps_4")}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }} container justifyContent="center">
        <QRCodeSVG id="qr-code-svg" style={{ height: 200, width: 200 }} value={`${process.env.REACT_APP_ENVIRONMENT_URL}/${tag}`} />
      </Grid>
    </Grid>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "90%",
            borderTopLeftRadius: "4%",
            borderTopRightRadius: "4%",
          },
        }}
      >
        <Puller />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
            {t("qr_code")}
          </Typography>

          {printForm}

          <Grid container spacing={2} mt={5}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
                onClick={handlePrint}
              >
                {t("print")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("qr_code")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={() => setOpen(false)} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>{printForm}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={() => setOpen(false)}
        >
          {t("close")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handlePrint}
        >
          {t("print")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagQRCode;
